import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { QueryApiContext } from './QueryContext';
import { AuthContext } from './AuthContext';
import { ActiveTenantContext, ActiveTenantContextType, TenantRole } from './ActiveTenantContext';
import { AuthObject } from '../utils/frontEndTypes';
import TenantChangedModal from '../components/tenantChangedModal/TenantChangedModal';

const ACTIVE_TENANT_KEY = 'activeTenantId';

export const ActiveTenantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const restQueryClient = useContext(QueryApiContext);
  const auth = useContext(AuthContext) as AuthObject;
  const userId = auth.user?.id;
  const [activeTenantId, setActiveTenantIdState] = useState<string | null>(() =>
    localStorage.getItem(ACTIVE_TENANT_KEY)
  );
  const [tenantChangedInOtherTab, setTenantChangedInOtherTab] = useState(false);

  // Use roles from AuthContext instead of fetching again
  const userRoles = useMemo(() => auth.user?.roles || [], [auth.user?.roles]);

  // Fetch role types
  const {
    data: roleTypesData,
    isLoading: isRoleTypesLoading,
    isFetched: isRoleTypesFetched
  } = restQueryClient.roleTypes.list.useQuery(
    ['roleTypes'],
    {},
    {
      enabled: !!userId
    }
  );

  // Memoize roleTypes to avoid unnecessary recalculations
  const roleTypes = useMemo(() => roleTypesData?.body || [], [roleTypesData?.body]);

  // Fetch all tenants (we'll filter to user's tenants in the useMemo)
  const {
    data: allTenantsData,
    isLoading: isTenantsLoading,
    isFetched: isTenantsFetched
  } = restQueryClient.tenants.list.useQuery(
    ['tenants'],
    {},
    {
      enabled: !!userId && userRoles.length > 0
    }
  );

  // Build available tenants list with role information
  const availableTenants = useMemo(() => {
    if (isRoleTypesLoading || isTenantsLoading) {
      return [];
    }

    const allTenants = allTenantsData?.body?.data || [];
    const tenants: TenantRole[] = [];

    // For each user role that's tenant-scoped, find the matching tenant
    userRoles.forEach((userRole) => {
      const roleType = roleTypes.find((rt) => rt.id === userRole.roleTypeId);
      if (!roleType || roleType.scope !== 'tenant') return;

      const tenantData = allTenants.find((t) => t.id === userRole.scopeId);
      if (!tenantData) return;

      tenants.push({
        tenantId: tenantData.id,
        tenantName: tenantData.name,
        roleTypeId: roleType.id,
        role: roleType.role as 'owner' | 'admin' | 'member'
      });
    });

    return tenants;
  }, [allTenantsData, userRoles, roleTypes, isRoleTypesLoading, isTenantsLoading]);

  // Set default active tenant if not set
  useEffect(() => {
    if (availableTenants.length > 0 && !activeTenantId) {
      const defaultTenantId = availableTenants[0].tenantId;
      setActiveTenantIdState(defaultTenantId);
      localStorage.setItem(ACTIVE_TENANT_KEY, defaultTenantId);
    }
  }, [availableTenants, activeTenantId]);

  // Validate active tenant is still valid
  useEffect(() => {
    if (activeTenantId && availableTenants.length > 0) {
      const isValid = availableTenants.some((t) => t.tenantId === activeTenantId);
      if (!isValid) {
        const defaultTenantId = availableTenants[0].tenantId;
        setActiveTenantIdState(defaultTenantId);
        localStorage.setItem(ACTIVE_TENANT_KEY, defaultTenantId);
      }
    }
    if (availableTenants.length === 0 && isRoleTypesFetched && isTenantsFetched) {
      setActiveTenantIdState(null);
      localStorage.removeItem(ACTIVE_TENANT_KEY);
    }
  }, [activeTenantId, availableTenants, isRoleTypesFetched, isTenantsFetched]);

  // Listen for cross-tab changes to the active tenant
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === ACTIVE_TENANT_KEY && e.oldValue !== null && e.newValue !== null && e.oldValue !== e.newValue) {
        setTenantChangedInOtherTab(true);
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const setActiveTenantId = useCallback((tenantId: string) => {
    setActiveTenantIdState(tenantId);
    localStorage.setItem(ACTIVE_TENANT_KEY, tenantId);
  }, []);

  const activeTenant = useMemo(
    () => availableTenants.find((t) => t.tenantId === activeTenantId) || null,
    [availableTenants, activeTenantId]
  );
  const activeTenantData = useMemo(() => {
    if (!activeTenantId || !allTenantsData?.body?.data) return null;
    return allTenantsData.body.data.find((t: { id: string }) => t.id === activeTenantId) || null;
  }, [activeTenantId, allTenantsData?.body]);

  // When the user has no roles, the tenants query is never enabled,
  // so isTenantsLoading stays true forever. Only count it as loading
  // if the query is actually enabled (user has roles).
  const isLoading = isRoleTypesLoading || (userRoles.length > 0 && isTenantsLoading);

  const value: ActiveTenantContextType = useMemo(
    () => ({
      activeTenantId,
      activeTenant,
      activeTenantData,
      availableTenants,
      roleTypes,
      setActiveTenantId,
      isLoading,
      tenantChangedInOtherTab
    }),
    [
      activeTenantId,
      activeTenant,
      activeTenantData,
      availableTenants,
      roleTypes,
      setActiveTenantId,
      isLoading,
      tenantChangedInOtherTab
    ]
  );

  return (
    <ActiveTenantContext.Provider value={value}>
      {children}
      {tenantChangedInOtherTab && <TenantChangedModal />}
    </ActiveTenantContext.Provider>
  );
};
